import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { useTheme } from 'styled-components'
import { connect } from 'react-redux'
import Div100vh from 'react-div-100vh'
import { useTranslation } from 'react-i18next'
import jwt_decode from 'jwt-decode'
import { useStatsigClient } from '@statsig/react-bindings'
import ModalActions from 'reducers/Modal'
import ReleaseActions from 'reducers/Releases'
import ReleaseModal from 'components/Modals/ReleaseModal'
import QuickViewModal from 'components/Modals/QuickViewModal'
import GlobalOverlaysWithRollout from 'components/GlobalOverlaysWithRollout'
import AppShellWithRollout from 'components/AppShellWithRollout'
import ResponsiveBreakpointsLegacyScope from 'components/ResponsiveBreakpointsLegacyScope'
import {
  RESPONSIVE_BREAKPOINT_VERSIONS,
  useResponsiveBreakpointsVersion
} from 'contexts/ResponsiveBreakpointsContext'
import { Playbar, Sidebar, DialogModal } from 'components'
import { RELEASE_QUICKVIEW_MODAL } from 'constants/featureFlags'
import useLocalStorage from 'utils/hooks/useLocalStorage'
import useNewPlaybar from 'utils/hooks/useNewPlaybar'
import UserVerificationBanner from 'components/Banner/UserVerificationBanner'
import { useDesktopMediaQuery } from 'utils/hooks/useMediaQueries'

const NEW_PLAYBAR_CONTENT_OFFSET = 'var(--playbar-content-bottom-offset, 140px)'

const mapStateToProps = ({ releases, setting, modal, global }) => ({
  selectedReleaseInModal: releases.selectedReleaseInModal,
  isSidebarOpen: setting.isSidebarOpen,
  showReleaseErrorModal: modal.showReleaseErrorModal,
  releaseErrorTitle: modal.releaseErrorTitle,
  releaseErrorMessage: modal.releaseErrorMessage,
  isIpadView: global.isIpadView
})

const MainLayoutContent = ({
  menu,
  content,
  isIpadView,
  isSidebarOpen,
  showReleaseErrorModal,
  releaseErrorTitle,
  releaseErrorMessage,
  selectedReleaseInModal,
  dispatch
}) => {
  const isDesktop = useDesktopMediaQuery()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const isAppShellV2 =
    responsiveBreakpointsVersion === RESPONSIVE_BREAKPOINT_VERSIONS.V2
  const theme = useTheme()
  const { t } = useTranslation()
  const { checkGate, isLoading: isStatsigLoading } = useStatsigClient()
  const [verificationBannerData, setVerificationBannerData] = useLocalStorage(
    'verificationBannerData',
    {}
  )
  const { shouldShow, lastClosed } = verificationBannerData
  const isQuickViewModalEnabled =
    !isStatsigLoading && checkGate(RELEASE_QUICKVIEW_MODAL)
  const isNewPlaybarEnabled = useNewPlaybar()

  useEffect(() => {
    const now = new Date().getTime()
    const twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000

    if (isEmpty(verificationBannerData)) {
      setVerificationBannerData({ shouldShow: true, lastClosed: now })
      return
    }

    if (now - lastClosed >= twentyFourHoursInMilliseconds) {
      setVerificationBannerData({ shouldShow: true, lastClosed: now })
    }
  }, [])

  const handleYesButton = useCallback(() => {
    dispatch(ModalActions.dismissReleaseError())
  }, [])

  const handleCloseReleaseModal = useCallback(() => {
    dispatch(ReleaseActions.toggleReleaseInModal(null))
  }, [])

  const handleBannerClose = () => {
    setVerificationBannerData({
      shouldShow: false,
      lastClosed: new Date().getTime()
    })
  }

  const renderReleaseModal = () => {
    if (!selectedReleaseInModal) {
      return null
    }

    const ReleaseModalComponent = isQuickViewModalEnabled
      ? QuickViewModal
      : ReleaseModal

    const releaseModal = (
      <div className="absolute flex w-full h-full justify-around items-center">
        <ReleaseModalComponent
          release={selectedReleaseInModal}
          isIpadView={isIpadView}
          onClickCloseButton={handleCloseReleaseModal} />
      </div>
    )

    return isQuickViewModalEnabled ? (
      <GlobalOverlaysWithRollout>
        {releaseModal}
      </GlobalOverlaysWithRollout>
    ) : (
      releaseModal
    )
  }

  const renderReleaseErrorModal = () => {
    if (showReleaseErrorModal) {
      return (
        <DialogModal
          hideCancel
          title={t(releaseErrorTitle)}
          okButtonTitle={t('general.ok')}
          onClickYes={handleYesButton}>
          <div className="w-full flex flex-col items-center mb-6">
            <div className="mt-12 mb-6">{t(releaseErrorMessage)}</div>
          </div>
        </DialogModal>
      )
    } else {
      return null
    }
  }

  const token = localStorage.getItem('accessToken')
  const currentUser = jwt_decode(token)
  const label = currentUser.label_staff
  const verifiedUser = currentUser.verified

  const showSideNav = isSidebarOpen || isDesktop
  const contentScrollStyle = isNewPlaybarEnabled
    ? { paddingBottom: NEW_PLAYBAR_CONTENT_OFFSET }
    : undefined

  return (
    <Div100vh>
      {/* need an id on mainLayout so that can use it to calculate items per row */}
      <div
        id="mainLayout"
        className={[
          'h-full w-full flex',
          isAppShellV2 ? 'min-w-0' : 'min-w-768'
        ].join(' ')}
        style={{ backgroundColor: theme.colors?.neutralW10 }}>
        {showSideNav && <Sidebar />}

        <div className="flex flex-col flex-1 min-w-0 relative overflow-hidden">
          {menu}
          <div
            className="relative flex-1 overflow-y-auto scrollbar scroll-smooth"
            style={contentScrollStyle}>
            <ResponsiveBreakpointsLegacyScope>
              {content}
            </ResponsiveBreakpointsLegacyScope>
          </div>
          {/* The redesigned playbar floats over the scroll area and lets
              clicks through to the content behind it; the legacy one is a
              sticky block in normal flow. */}
          <div
            className={
              isNewPlaybarEnabled
                ? 'absolute bottom-0 left-0 right-0 w-full z-30'
                : 'flex-none -bottom-1 w-full z-30'
            }
            style={
              isNewPlaybarEnabled
                ? { pointerEvents: 'none' }
                : { position: 'sticky', bottom: 0, zIndex: 1 }
            }>
            <ResponsiveBreakpointsLegacyScope>
              <Playbar />
            </ResponsiveBreakpointsLegacyScope>
            {!label && !verifiedUser && shouldShow && (
              <UserVerificationBanner onClose={handleBannerClose} />
            )}
          </div>
        </div>
        <ResponsiveBreakpointsLegacyScope>
          {renderReleaseModal()}
          {renderReleaseErrorModal()}
        </ResponsiveBreakpointsLegacyScope>
      </div>
    </Div100vh>
  )
}

const mainLayoutPropTypes = {
  menu: PropTypes.element,
  content: PropTypes.element,
  isIpadView: PropTypes.bool,
  isSidebarOpen: PropTypes.bool,
  showReleaseErrorModal: PropTypes.bool,
  releaseErrorTitle: PropTypes.string,
  releaseErrorMessage: PropTypes.string,
  selectedReleaseInModal: PropTypes.object,

  dispatch: PropTypes.func
}

MainLayoutContent.propTypes = mainLayoutPropTypes

const MainLayout = (props) => (
  <AppShellWithRollout>
    <MainLayoutContent
      menu={props.menu}
      content={props.content}
      isIpadView={props.isIpadView}
      isSidebarOpen={props.isSidebarOpen}
      showReleaseErrorModal={props.showReleaseErrorModal}
      releaseErrorTitle={props.releaseErrorTitle}
      releaseErrorMessage={props.releaseErrorMessage}
      selectedReleaseInModal={props.selectedReleaseInModal}
      dispatch={props.dispatch}
    />
  </AppShellWithRollout>
)

MainLayout.propTypes = mainLayoutPropTypes

export default connect(mapStateToProps)(MainLayout)
